import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { ScoreRing } from '../../dashboard';
import type { ProgressTier } from '../../dashboard/colors';
import type { FacilityMetricsResult } from '../../../services/api/facilityMetricsService';
import type { Facility } from '../utils/utils';
import { getActionCopy } from '../utils/readinessUtils';
import SetupStep from './SetupStep';
import OnboardingTrialHeader from './TrialHeader';

interface OnboardingOverviewProps {
    facility: Facility;
    metrics: FacilityMetricsResult;
    showNextAction?: boolean;
}

export default function OnboardingOverview({
    facility,
    metrics,
    showNextAction = true,
}: OnboardingOverviewProps) {
    const { t } = useTranslation();

    const ONBOARDING_TIERS: readonly ProgressTier[] = [
        { min: 75, color: '#10b981', label: t('facilities.onboarding.overview.tiers.ready') },
        { min: 50, color: '#fbbf24', label: t('facilities.onboarding.overview.tiers.inProgress') },
        { min: 0, color: '#d4d4d8', label: t('facilities.onboarding.overview.tiers.gettingStarted') },
    ];

    const signals = Array.isArray(metrics.signals) ? metrics.signals : [];
    const checklistSignals = signals.filter((s) => s.met != null);
    const remaining = checklistSignals.filter((s) => s.met === false);
    const nextSignal = remaining[0];

    return (
        <div className="mb-12">
            <OnboardingTrialHeader
                facility={facility}
                showDescription={showNextAction}
            />
            <Card className="border-subtle">
                <CardContent className="p-0">
                    <div className="flex flex-col gap-6 p-5 sm:flex-row sm:items-center">
                        <ScoreRing
                            title={t('facilities.onboarding.overview.readiness')}
                            score={metrics.score}
                            layout="vertical"
                            tiers={ONBOARDING_TIERS}
                            className="px-4"
                        />

                        <div className="min-w-0 flex-1">
                            <h2 className="mb-3 text-sm font-semibold text-foreground">
                                {t('facilities.onboarding.overview.setupChecklist')}
                            </h2>
                            <div className="divide-y divide-white/10 rounded-md border border-subtle">
                                {checklistSignals.map((signal) => {
                                    const labelKey = `facilities.onboarding.signalLabels.${signal.key}`;
                                    const labelOverride = t(labelKey, { defaultValue: '' });
                                    return (
                                        <SetupStep
                                            key={signal.key}
                                            signal={labelOverride ? { ...signal, label: labelOverride } : signal}
                                        />
                                    );
                                })}
                            </div>
                            {showNextAction && nextSignal && (
                                <div className="mt-3 flex items-start gap-2.5 rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2">
                                    <ArrowRight
                                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400"
                                        aria-hidden="true"
                                    />
                                    <span className="text-xs text-foreground">
                                        <span className="font-medium text-emerald-400">
                                            {t('facilities.onboarding.overview.next')}{' '}
                                        </span>
                                        {getActionCopy(nextSignal, t)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
